import { useState, useEffect, useCallback } from 'react';
import { hrms } from '../../services/api';
import Card from '../../components/common/Card';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import { theme } from '../../theme';

const EMPTY_FORM = {
  employee_id: '', effective_date: '', grade: '', band: '',
  basic: '', hra: '', medical_allowance: '', travel_allowance: '', special_allowance: '',
  pf_deduction: '', tds_deduction: '', other_deduction: '',
  remarks: '', status: 'active',
};

function PayFixation() {
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [filterEmp, setFilterEmp] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pf, em] = await Promise.allSettled([
        hrms.getPayFixations(filterEmp ? { employee_id: filterEmp } : {}),
        hrms.getEmployees({ limit: 500 }),
      ]);
      if (pf.status === 'fulfilled') setRecords(pf.value.data || []);
      if (em.status === 'fulfilled') {
        const d = em.value.data;
        setEmployees(Array.isArray(d) ? d : d.items || []);
      }
    } catch { } finally { setLoading(false); }
  }, [filterEmp]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditTarget(null); setForm(EMPTY_FORM); setModal(true); };
  const openEdit = (rec) => {
    setEditTarget(rec);
    setForm({
      employee_id: String(rec.employee_id),
      effective_date: rec.effective_date || '',
      grade: rec.grade || '',
      band: rec.band || '',
      basic: String(rec.basic || 0),
      hra: String(rec.hra || 0),
      medical_allowance: String(rec.medical_allowance || 0),
      travel_allowance: String(rec.travel_allowance || 0),
      special_allowance: String(rec.special_allowance || 0),
      pf_deduction: String(rec.pf_deduction || 0),
      tds_deduction: String(rec.tds_deduction || 0),
      other_deduction: String(rec.other_deduction || 0),
      remarks: rec.remarks || '',
      status: rec.status || 'active',
    });
    setModal(true);
  };

  const closeModal = () => { setModal(false); setEditTarget(null); setForm(EMPTY_FORM); };

  const f = (k) => Number(form[k] || 0);
  const computedGross = f('basic') + f('hra') + f('medical_allowance') + f('travel_allowance') + f('special_allowance');
  const computedNet = computedGross - (f('pf_deduction') + f('tds_deduction') + f('other_deduction'));

  const handleSave = async () => {
    if (!form.employee_id || !form.effective_date) { alert('Employee and Effective Date are required.'); return; }
    setSaving(true);
    try {
      const payload = {
        employee_id: Number(form.employee_id),
        effective_date: form.effective_date,
        grade: form.grade || null,
        band: form.band || null,
        basic: f('basic'), hra: f('hra'),
        medical_allowance: f('medical_allowance'),
        travel_allowance: f('travel_allowance'),
        special_allowance: f('special_allowance'),
        pf_deduction: f('pf_deduction'),
        tds_deduction: f('tds_deduction'),
        other_deduction: f('other_deduction'),
        remarks: form.remarks || null,
        status: form.status,
      };
      if (editTarget) {
        await hrms.updatePayFixation(editTarget.id, payload);
      } else {
        await hrms.createPayFixation(payload);
      }
      closeModal();
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pay fixation record?')) return;
    try { await hrms.deletePayFixation(id); load(); } catch { alert('Failed to delete.'); }
  };

  const cols = [
    { header: 'Employee', key: 'employee_name', render: (v) => v || '—' },
    { header: 'Effective Date', key: 'effective_date', render: (v) => v ? new Date(v + 'T12:00:00').toLocaleDateString() : '—' },
    { header: 'Grade', key: 'grade', render: (v) => v || '—' },
    { header: 'Band', key: 'band', render: (v) => v || '—' },
    { header: 'Basic (₹)', key: 'basic', render: (v) => Number(v).toLocaleString() },
    { header: 'Gross (₹)', key: 'gross_salary', render: (v) => <strong>₹{Number(v).toLocaleString()}</strong> },
    { header: 'Net (₹)', key: 'net_salary', render: (v) => <strong style={{ color: theme.success }}>₹{Number(v).toLocaleString()}</strong> },
    { header: 'Status', key: 'status', render: (v) => <Badge status={v} /> },
    {
      header: 'Actions', key: 'id', align: 'right',
      render: (id, row) => (
        <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
          <Button size="sm" variant="outline" onClick={() => openEdit(row)}>Edit</Button>
          <Button size="sm" variant="danger" onClick={() => handleDelete(id)}>Delete</Button>
        </div>
      ),
    },
  ];

  const inp = { width: '100%', padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', marginBottom: '0.75rem', boxSizing: 'border-box', background: theme.white };
  const lbl = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.35rem' };
  const sectionHdr = { fontSize: '0.75rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0.5rem 0 0.75rem', paddingBottom: '0.4rem', borderBottom: `1px solid ${theme.border}` };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Pay Fixation</h1>
        <Button variant="primary" onClick={openCreate}>+ New Pay Fixation</Button>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
        <select style={{ ...inp, marginBottom: 0, width: '260px' }} value={filterEmp} onChange={(e) => setFilterEmp(e.target.value)}>
          <option value="">All Employees</option>
          {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name} ({e.emp_code})</option>)}
        </select>
      </div>

      <Card padding="0">
        <Table columns={cols} data={records} loading={loading} emptyMessage="No pay fixation records found" />
      </Card>

      <Modal isOpen={modal} onClose={closeModal} title={editTarget ? 'Edit Pay Fixation' : 'New Pay Fixation'}
        footer={<><Button variant="outlineGray" onClick={closeModal}>Cancel</Button><Button variant="primary" loading={saving} onClick={handleSave}>Save</Button></>}>

        <label style={lbl}>Employee *</label>
        <select style={inp} value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}>
          <option value="">Select employee</option>
          {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name} ({e.emp_code})</option>)}
        </select>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <label style={lbl}>Effective Date *</label>
            <input style={inp} type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} />
          </div>
          <div>
            <label style={lbl}>Status</label>
            <select style={inp} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="active">Active</option>
              <option value="revised">Revised</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label style={lbl}>Grade</label>
            <input style={inp} value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} placeholder="e.g. L3" />
          </div>
          <div>
            <label style={lbl}>Band</label>
            <input style={inp} value={form.band} onChange={(e) => setForm({ ...form, band: e.target.value })} placeholder="e.g. B2" />
          </div>
        </div>

        <p style={sectionHdr}>Earnings</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
          {[['basic', 'Basic'], ['hra', 'HRA'], ['medical_allowance', 'Medical Allowance'], ['travel_allowance', 'Travel Allowance'], ['special_allowance', 'Special Allowance']].map(([k, label]) => (
            <div key={k}>
              <label style={lbl}>{label} (₹)</label>
              <input style={inp} type="number" min="0" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder="0" />
            </div>
          ))}
        </div>

        <p style={sectionHdr}>Deductions</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
          {[['pf_deduction', 'PF Deduction'], ['tds_deduction', 'TDS Deduction'], ['other_deduction', 'Other Deduction']].map(([k, label]) => (
            <div key={k}>
              <label style={lbl}>{label} (₹)</label>
              <input style={inp} type="number" min="0" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder="0" />
            </div>
          ))}
        </div>

        {/* Summary */}
        <div style={{ background: theme.lightBg, borderRadius: '0.5rem', padding: '0.875rem 1rem', border: `1px solid ${theme.primaryLight}`, display: 'flex', justifyContent: 'space-around', marginBottom: '0.75rem' }}>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '0.7rem', color: theme.textMuted, margin: 0, fontWeight: 600, textTransform: 'uppercase' }}>Gross Salary</p>
            <p style={{ fontSize: '1.1rem', fontWeight: 700, color: theme.primary, margin: '0.25rem 0 0' }}>₹{computedGross.toLocaleString()}</p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '0.7rem', color: theme.textMuted, margin: 0, fontWeight: 600, textTransform: 'uppercase' }}>Net Salary</p>
            <p style={{ fontSize: '1.1rem', fontWeight: 700, color: theme.success, margin: '0.25rem 0 0' }}>₹{computedNet.toLocaleString()}</p>
          </div>
        </div>

        <label style={lbl}>Remarks</label>
        <textarea style={{ ...inp, resize: 'vertical', minHeight: '70px' }} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} placeholder="Optional notes..." />
      </Modal>
    </div>
  );
}

export default PayFixation;
